'use client';

import React from 'react';
import NextImage from 'next/image';

interface ImageProps {
  src: string;                    // Required - image path
  alt: string;                    // Required - SEO critical
  width?: number;                 // Optional but recommended
  height?: number;                // Optional but recommended
  caption?: string;               // Optional - adds context
  priority?: boolean;             // Optional - for above-fold images
  fill?: boolean;                 // Optional - image fills container
  sizes?: string;                 // Optional - responsive sizing
  className?: string;             // Optional - additional styles
}

export const Image: React.FC<ImageProps> = ({
  src,
  alt,
  width,
  height,
  caption,
  priority = false,
  fill = false,
  sizes,
  className = ''
}) => {
  // Validate image path exists (basic check)
  if (!src || typeof src !== 'string') {
    console.warn('Image component: Invalid src provided');
    return null;
  }

  // Console warnings for missing dimensions when not using fill
  if (!fill && (!width || !height)) {
    console.warn(`Image component: width and height recommended for image "${src}" to prevent layout shift`);
  }

  // Smart default for sizes
  const defaultSizes = fill 
    ? '100vw' 
    : sizes || '(max-width: 768px) 100vw, 800px';

  // Prepare Next.js Image props
  const defaultClassName = fill ? 'object-cover' : 'w-full h-auto';
  const finalClassName = className ? `${defaultClassName} ${className}` : defaultClassName;
  
  const imageProps: any = {
    src,
    alt,
    priority,
    sizes: defaultSizes,
    className: finalClassName
  };

  // Add dimensions or fill prop
  if (fill) {
    imageProps.fill = true;
  } else {
    if (width) imageProps.width = width;
    if (height) imageProps.height = height;
  }

  return (
    <figure className={className}>
      {fill ? (
        <div className="relative">
          <NextImage {...imageProps} />
        </div>
      ) : (
        <NextImage {...imageProps} />
      )}
      {caption && (
        <figcaption className="mt-3 text-sm text-gray-600 text-center">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};